import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import cx from 'classnames';

import { isAtHomeNode } from "state/node/selectors";
import { getSearchQuery } from "state/search/selectors";
import { Page } from "ui/page/Page";
import MobileBack from "ui/page/MobileBack";
import BackBox from "ui/page/BackBox";
import BackBoxInner from "ui/page/BackBoxInner";
import { useHomeNews } from "ui/feed/feeds";
import { useSearchSuggestions } from "ui/mainmenu/search/search-suggestions";
import SearchInput from "ui/mainmenu/search/SearchInput";
import SearchSuggestions from "ui/mainmenu/search/SearchSuggestions";
import BottomMenu from "ui/mainmenu/BottomMenu";
import { useOverlay } from "ui/overlays/overlays";
import SearchTabs from "ui/search/SearchTabs";
import SearchFeed from "ui/search/SearchFeed";
import { REL_CURRENT } from "util/rel-node-name";

export default function MobileSearchPage() {
    const atHomeNode = useSelector(isAtHomeNode);
    const searchQuery = useSelector(getSearchQuery);
    const newsHref = useHomeNews();
    const pageRef = useRef(null);
    const [suggestions, setSuggestions] = useState<boolean>(!searchQuery);

    const backHref = suggestions ? (atHomeNode ? newsHref : "/") : "";

    const onSubmit = useCallback(() => setSuggestions(false), [setSuggestions]);

    const {
        query, searchList, selectedIndex, focused, handleKeyDown, handleChange, handleFocus, handleBlur, handleClear,
        handleClick, handleHistoryDelete, inputDom, listDom
    } = useSearchSuggestions({onSubmit});

    useEffect(() => {
        setSuggestions(!searchQuery);
        if (!searchQuery) {
            inputDom.current?.focus();
        }
    }, [inputDom, searchQuery]);

    useEffect(() => {
        if (focused) {
            setSuggestions(true);
        }
    }, [focused]);

    useOverlay(
        pageRef,
        {
            visible: !suggestions,
            onClose: () => setSuggestions(true),
            closeOnClick: false,
            closeOnSelect: false,
            closeOnEscape: false,
            disableScroll: false
        }
    )

    const results = !suggestions && !focused;

    return (
        <>
            <Page className={cx("search-page", {"tabbed-page": results})}>
                <main className="page-central-pane" ref={pageRef}>
                    <MobileBack nodeName={REL_CURRENT} href={backHref} sticky>
                        <div id="search-box">
                            <SearchInput
                                query={query}
                                handleKeyDown={handleKeyDown}
                                handleChange={handleChange}
                                handleFocus={handleFocus}
                                handleBlur={handleBlur}
                                handleClear={handleClear}
                                ref={inputDom}
                            />
                        </div>
                    </MobileBack>
                    {results &&
                        <BackBox>
                            <BackBoxInner>
                                <SearchTabs/>
                            </BackBoxInner>
                        </BackBox>
                    }
                    <SearchSuggestions
                        query={query}
                        searchList={searchList}
                        selectedIndex={selectedIndex}
                        handleClick={handleClick}
                        onHistoryDelete={handleHistoryDelete}
                        visible={!results && (searchList.length > 0 || !!query)}
                        ref={listDom}
                    />
                    {results && <SearchFeed/>}
                </main>
            </Page>
            <BottomMenu/>
        </>
    );
}
